import { inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';
import { OnboardingService } from '../services/onboarding.service';

/** Planning Center's Firebase OIDC provider id. */
const PCO_PROVIDER_ID = 'oidc.planningcenteronline.com';

/**
 * Where a user with no organisation document belongs.
 *
 * The same split OrganizationGuard makes: a Planning Center user's organisation
 * comes from their sign-in claim, so they finish at /organization-setup; a
 * standalone user creates one at /auth/register.
 */
function setupRouteFor(user: { providerData?: { providerId: string }[] }): string {
    return user.providerData?.some(p => p.providerId === PCO_PROVIDER_ID)
        ? '/organization-setup'
        : '/auth/register';
}

export const onboardingGuard: CanActivateFn = async (route, state) => {
    const auth = inject(Auth);
    const firestore = inject(Firestore);
    const router = inject(Router);
    const injector = inject(EnvironmentInjector);
    const onboardingService = inject(OnboardingService);
    const authState$ = authState(auth);

    try {
        const user = await firstValueFrom(authState$);

        if (!user) {
            return true; // Let auth guard handle authentication
        }

        // Get user's organization ID
        const userDoc = await runInInjectionContext(injector, () => getDoc(doc(firestore, 'users', user.uid)));
        if (!userDoc.exists()) {
            return true; // Let organization guard handle this
        }

        const userData = userDoc.data();
        const organizationId = userData['organizationId'];

        if (!organizationId) {
            return true; // Let organization guard handle this
        }

        // Read organization document
        const orgDoc = await runInInjectionContext(injector, () => getDoc(doc(firestore, 'organizations', organizationId)));
        if (!orgDoc.exists()) {
            // An id with no organisation behind it is an unfinished signup, not
            // a finished one. OrganizationGuard cannot catch this: it only
            // checks that the *field* is set, and the Planning Center sign-in
            // blocking function sets it from the OIDC claim before the
            // organisation is ever created. Letting it through was how someone
            // whose setup failed landed on the dashboard by refreshing.
            const target = setupRouteFor(user);
            console.warn(`Organization ${organizationId} does not exist — redirecting to ${target}`);
            router.navigate([target]);
            return false;
        }

        const orgData = orgDoc.data();
        let onboarding = orgData['onboarding'];

        // Auto-migrate orgs that existed before the onboarding system
        if (!onboarding) {
            onboarding = await onboardingService.migrateExistingOrg(organizationId, orgData);
        }

        // Resolves against this organisation's own step list, and drops the
        // WhatsApp-dependent steps when no number is connected.
        const nextStep = await onboardingService.resolveNextStep(organizationId);

        if (nextStep) {
            console.log(`Onboarding incomplete — redirecting to ${nextStep.route}`);
            router.navigate([nextStep.route]);
            return false;
        }

        return true;
    } catch (error) {
        console.error('Error in onboarding guard:', error);
        return true; // Fail open to avoid blocking users on transient errors
    }
};
